import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { ShoppingCart } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaCartShopping } from "react-icons/fa6";
import axios from "axios";

const GetBook = ({ handleClick2, size2, setShow2 }) => {
  const [searchText, setSearchText] = React.useState("");
  const { t, i18n } = useTranslation();
  const [dataSource, setDataSource] = React.useState([]);
  const [deleteItemId, setDeleteItemId] = React.useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const token = localStorage.getItem("token");

  React.useEffect(() => {
    getApiProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";

  const getApiProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://api.clever-school.com/api/books",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setDataSource(
        response.data.map((item, index) => ({
          index: index + 1,
          id: item.id,
          name: item.name,
          class_id: item.class_id,
          stock: item.stock,
          orders: item.orders,
          price: item.price,
        }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmedDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `https://api.clever-school.com/api/books/${deleteItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await getApiProducts();
      setDeleteItemId(null);
      setIsDeleteModalVisible(false);
    } catch (error) {
      console.error("Error deleting product:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "index", headerName: "#", width: 20 },
    { field: "name", headerName: t("name"), width: 140 },
    { field: "class_id", headerName: t("class"), width: 70 },
    { field: "stock", headerName: t("stock"), width: 100 },
    { field: "price", headerName: t("price"), width: 120 },
    { field: "orders", headerName: t("orders"), width: 100 },
    {
      field: "action",
      headerName: t("action"),
      width: 240,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Link>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ShoppingCart />}
              onClick={() => handleClick2(params.row)}
              disabled={params.row.stock === 0}
              sx={{
                backgroundColor: "#4caf50",
                "&:hover": { backgroundColor: "#388e3c" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>

          <Link
            to={`/get-books/UpdateBook/${params.row.id}`}
            style={{ textDecoration: "none" }}
          >
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: "#3f51b5",
                "&:hover": { backgroundColor: "#303f9f" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>
          <Link>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleDelete(params.row.id)}
              sx={{
                backgroundColor: "#d32f2f",
                "&:hover": { backgroundColor: "#b71c1c" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>
        </div>
      ),
    },
  ];

  const filteredRows = dataSource.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <div style={{ margin: "25px", overflow: "hidden" }}>
      <Box
        sx={{
          direction,
          textAlign,
          marginBottom: "16px",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <TextField
          placeholder={t("searchHere")}
          variant="outlined"
          sx={{
            flex: "1",
            minWidth: "250px",
            maxWidth: "400px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#ddd" },
              "&:hover fieldset": { borderColor: "#999" },
              "&.Mui-focused fieldset": { borderColor: "#3f51b5" },
            },
          }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <Link to="/AddBook" style={{ textDecoration: "none" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: "#3f51b5",
                color: "#fff",
                "&:hover": { backgroundColor: "#303f9f" },
                borderRadius: "6px",
                padding: "10px 20px",
                textTransform: "none",
              }}
            >
              {t("addBook")}
            </Button>
          </Link>
          <Link to="/ShoppingBook" style={{ textDecoration: "none" }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#2196f3",
                color: "#fff",
                "&:hover": { backgroundColor: "#1976d2" },
                borderRadius: "6px",
                padding: "10px 20px",
                textTransform: "none",
                position: "relative",
              }}
            >
              {t("Shopping")}
              <FaCartShopping
                style={{
                  fontSize: 24,
                  marginLeft: "8px",
                  color: "#fff",
                }}
              />
              <div
                className="cart"
                onClick={() => setShow2(false)}
                data-count={size2}
              />
            </Button>
          </Link>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
          }}
        >
          <CircularProgress size={60} sx={{ color: "#3f51b5" }} />
          <Typography variant="h6" sx={{ marginTop: 2 }}>
            {t("Loading Books...")}
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            marginTop: 2,
            marginBottom: 10,
            padding: 2,
            overflowX: "auto",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <DataGrid
            rows={filteredRows}
            columns={columns}
            autoHeight
            pagination={false}
            disableSelectionOnClick
            sx={{
              minWidth: "800px",
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#3f51b5",
                color: "black",
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "16px",
                  fontWeight: "bold",
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-virtualScroller": {
                minHeight: "200px !important",
              },
            }}
          />
        </Paper>
      )}

      <Dialog
        open={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
      >
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogContent>
          <p>{t("confirmDeleteMessage")}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteModalVisible(false)}>
            {t("cancel")}
          </Button>
          <Button onClick={handleConfirmedDelete} color="error">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GetBook;
